import { observer } from "mobx-react-lite";
import usePage from "../../../../../../components/layouts/base-page-layout/page-hook";
import { useCallback, useEffect, useState } from "react";
import useTranslation from "../../../../../../translation/hooks/translation-hook";
import useNavigationBlocker from "../../../../../../hooks/navigation-blocker-hook";
import TranslationScope from "../../../../../../translation/translation-scope";
import EditorPageLayout from "../../../../../../components/layouts/editor-page/editor-page-layout";
import { ConfirmModal } from "../../../../../../components/modal/components/confirm-modal/confirm-modal";
import ControlGroupWithPreview from "../../../../../../components/layouts/control-group-with-preview/control-group-with-preview";
import ListLayout from "../../../../../../components/layouts/list-layout/list-layout";
import { FormField } from "../../../../../../components/form-field/form-field";
import TranslationInput from "../../../../../../translation/translation-input/translation-input";
import { RETURNS_PORTAL_TRANSLATION_KEYS } from "../../constants";
import LocateGiftPreview1 from '../../../../../../images/previews/locate-gift-1.svg';
import LocateGiftPreview2 from '../../../../../../images/previews/locate-gift-2.svg';
import TranslationTextarea from "../../../../../../translation/translation-textarea/translation-textarea";

const GiftReturnsEditorPage = observer(() => {
    const { setSubTitles } = usePage();
    const [isShowUnsaved, setIsShowUnsaved] = useState(false);

    useEffect(() => {
        setSubTitles(['Portal', 'Content', 'Return Portal', 'Gift Returns']);
    }, [setSubTitles]);

    const {
        hasChanges: hasTranslationChanges,
        discardChanges: translationsDiscardChanges,
        save: saveTranslations,
        selectedLocale
    } = useTranslation();

    const hasChanges = () => {
        return hasTranslationChanges();
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const discardChanges = () => {
        translationsDiscardChanges();
    }

    const onSaveClicked = () => {
        const promised = [];

        if (hasTranslationChanges()) {
            promised.push(
                saveTranslations()
            );
        }

        Promise.all(promised).then(() => {
            unblockNavigation();
        })
    }

    return (
        <TranslationScope scope="assistant" locale={selectedLocale}>
            <EditorPageLayout
                toolbar={{
                    saveButton: {
                        hasChanges: hasChanges(),
                        disabled: !hasChanges(),
                        onClick: onSaveClicked
                    }
                }}
            >
                <ListLayout>
                    <ControlGroupWithPreview hasTopBorder={false} preview={<img src={LocateGiftPreview1} alt="" />}>
                        <ListLayout>
                            <FormField label="Header">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.RETURN_GIFTS} />
                            </FormField>

                            <FormField label="Order Details Header">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.ORDER_DETAILS} />
                            </FormField>

                            <FormField label="Description">
                                <TranslationTextarea translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.ORDER_DETAILS_CONTENT} />
                            </FormField>

                            <FormField label="Order Number Hint">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.GIFT_ORDER_NUMBER_FIELD} />
                            </FormField>

                            <FormField label="Customer Identifier Hint">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.GIFT_EZP_FIELD} />
                            </FormField>

                            <FormField label="Recipient Information Header">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.GIFT_RECIPIENTS_INFORMATION} />
                            </FormField>

                            <FormField label="Recipient Information Instructions">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.GIFT_RECIPIENTS_INFORMATION_CONTENT} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview hasTopBorder={false} preview={<img src={LocateGiftPreview2} alt="" />}>
                        <ListLayout>
                            <FormField label="Full Name Hint">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.GIFT_RECIPIENTS_FULL_NAME_FIELD} />
                            </FormField>

                            <FormField label="Email Address Hint">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.GIFT_RECIPIENTS_EMAIL_FIELD} />
                            </FormField>

                            <FormField label="Button Text">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.LOCATE_MY_GIFT} />
                            </FormField>

                            <FormField label="Regular Order Return Link Text">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.RETURNING_YOUR_ORDER} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>
                </ListLayout>
            </EditorPageLayout>

            {isShowUnsaved &&
                <ConfirmModal
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => setIsShowUnsaved(false)
                    }}
                    okButton={{
                        title: 'Discard',
                        onClick: () => {
                            discardChanges();
                            setIsShowUnsaved(false);
                            retryNavigation();
                        }
                    }}
                />
            }
        </TranslationScope>
    );
});

export default GiftReturnsEditorPage;